import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { routes } from 'app/routing/routesConfig';
import {
  fetchContributionsListEffect,
  fetchSongEffect,
} from 'app/store/SongList/effects';
import { getContributionsListLoadingState } from 'app/store/SongList/selectors';
import { LoadingState } from 'types/enums';
import {
  SongFolderChangedPayload,
  SubscriptionPayload,
  WsJsonMessage,
} from 'types/websocket.types';
import {
  fetchInvitesEffect,
  fetchNotificationsEffect,
  fetchUnseenInviteCountEffect,
  fetchUnseenNotificationCountEffect,
} from 'app/store/Notifications/effects';
import {
  handleWebSocketMessage,
  setSubscriptionResource,
} from 'app/store/WebSockets/slice';
import { updateSongFolderAction } from 'app/store/SongList/slice';
import { handleResourceUpdate } from './handleResourceUpdate';
import { isPageView } from './utils';

export function websocketsMiddleware(store: any) {
  return (next: Dispatch) => (action: AnyAction) => {
    const { dispatch, getState } = store;
    const result = next(action);

    if (action.type === handleWebSocketMessage.type) {
      const wsJsonMessage = action.payload as WsJsonMessage;
      switch (wsJsonMessage.type) {
        case 'InviteReceived': {
          if (isPageView(routes.notifications)) {
            dispatch(fetchInvitesEffect());
          }

          break;
        }
        case 'InAppNotificationReceived': {
          if (isPageView(routes.notifications)) {
            dispatch(fetchNotificationsEffect());
          }
          break;
        }

        case 'UnreadInviteCountChanged': {
          if (!isPageView(routes.notifications)) {
            dispatch(fetchUnseenInviteCountEffect());
          }
          break;
        }

        case 'UnreadInAppNotificationCountChanged': {
          if (!isPageView(routes.notifications)) {
            dispatch(fetchUnseenNotificationCountEffect());
          }
          break;
        }

        case 'SongContributionAdded': {
          const contributionsListLoadingState = getContributionsListLoadingState(
            getState()
          );
          const isContributionsListView = isPageView(routes.songContributions);
          const isNotFetching =
            contributionsListLoadingState !== LoadingState.FETCHING;

          if (isContributionsListView && isNotFetching) {
            dispatch(fetchContributionsListEffect({ update: true }));
          }
          break;
        }

        case 'Subscription': {
          const wsPayload = wsJsonMessage.payload as SubscriptionPayload;
          const { status, subscriptionResource } = wsPayload;
          const songRoomViewMatch = isPageView<'guid'>(routes.song);
          if (songRoomViewMatch) {
            dispatch(setSubscriptionResource({ subscriptionResource }));

            if (
              status === 'Update' &&
              !subscriptionResource &&
              songRoomViewMatch.params.guid
            ) {
              dispatch(
                fetchSongEffect({
                  guid: songRoomViewMatch.params.guid,
                })
              );
            }
          }
          break;
        }
        case 'SongFolderChanged': {
          const wsPayload = wsJsonMessage.payload as SongFolderChangedPayload;
          dispatch(updateSongFolderAction(wsPayload));
          break;
        }
        case 'ResourceUpdate': {
          handleResourceUpdate(wsJsonMessage, dispatch, getState);
          break;
        }
        default:
          break;
      }
    }

    return result;
  };
}
